import React, { useEffect, useState } from "react";
import { FaTimes, FaTools, FaClock, FaCheckCircle } from "react-icons/fa";
import { GrStatusGood } from "react-icons/gr";
import { useTranslation } from "react-i18next";
import { getProtected } from "../../network/ApiService";
import { API_END_POINTS } from "../../network/apiEndPoint";
import {useNavigate} from 'react-router-dom'

const ViewReport = (props) => {
  const { showModal, setShowModal } = props;
  const navigate = useNavigate() ; 
  const [report, setReport] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('condition'); // 'condition', 'maintenance', 'utilization'
  const { t } = useTranslation();

  // Condition mapping based on the menus array
  const conditionMapping = {
    "": "Select technical condition",
    2: "Very good",
    3: "Good", 
    4: "Used",
    5: "Bad",
    6: "Very bad",
    7: "New"
  };

  // Color mapping for different conditions
  const getConditionStyle = (conditionKey) => {
    switch(parseInt(conditionKey)) {
      case 2: // Very good
        return {
          bgColor: "bg-green-100",
          borderColor: "border-green-300",
          textColor: "text-green-800",
          valueColor: "text-green-600",
          emoji: "🌟"
        };
      case 3: // Good
        return {
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          textColor: "text-green-700",
          valueColor: "text-green-600",
          emoji: "✅"
        };
      case 4: // Used
        return {
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          textColor: "text-blue-700",
          valueColor: "text-blue-600",
          emoji: "🔧"
        };
      case 5: // Bad
        return {
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
          textColor: "text-orange-700",
          valueColor: "text-orange-600",
          emoji: "⚠️"
        };
      case 6: // Very bad
        return {
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-700",
          valueColor: "text-red-600",
          emoji: "🔴"
        };
      case 7: // New
        return {
          bgColor: "bg-emerald-50",
          borderColor: "border-emerald-200",
          textColor: "text-emerald-700",
          valueColor: "text-emerald-600",
          emoji: "🆕"
        };
      default: // Unknown/Select condition
        return {
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          textColor: "text-gray-700",
          valueColor: "text-gray-600",
          emoji: "❓"
        };
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      if (!showModal) return;
      
      setLoading(true);
      try {
        const response = await getProtected(API_END_POINTS.assetSummary);
        if (response) {
          setReport(response);
        }
      } catch (error) {
         if (error.response?.status === 401) {
          navigate("/login");
          return ; 
      }
        console.error("Error fetching report:", error);
        setError(t('viewReport.errorMessage') || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [showModal, t]);

  const renderConditionReport = () => {
    const conditionSummary = report?.condition_summary || {};
    
    // Filter out empty keys and sort by condition value
    const validConditions = Object.entries(conditionSummary)
      .filter(([key, count]) => key !== "" && count > 0)
      .sort(([a], [b]) => parseInt(a) - parseInt(b));

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('viewReport.condition.title') || 'Asset Condition Report'}
          </h3>
          <p className="text-gray-600 text-sm">
            {t('viewReport.condition.subtitle') || 'Overview of asset conditions across your inventory'}
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {validConditions.map(([conditionKey, count]) => {
            const style = getConditionStyle(conditionKey);
            const conditionLabel = conditionMapping[parseInt(conditionKey)] || 'Unknown';
            
            return (
              <div 
                key={conditionKey}
                className={`${style.bgColor} border ${style.borderColor} rounded-lg p-6 text-center transition-all hover:shadow-md`}
              >
                <div className="text-3xl mb-3">{style.emoji}</div>
                <div className="space-y-2">
                  <h4 className={`font-medium ${style.textColor}`}>
                    {conditionLabel}
                  </h4>
                  <div className={`text-2xl font-bold ${style.valueColor}`}>
                    {count.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t('viewReport.common.assets') || 'Assets'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderUtilizationReport = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('viewReport.utilization.title') || 'Asset Utilization Report'}
        </h3>
        <p className="text-gray-600 text-sm">
          {t('viewReport.utilization.subtitle') || 'Operating hours and utilization statistics'}
        </p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
          <div className="text-3xl mb-3">⏱️</div>
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">
              {t('viewReport.utilization.totalHours') || 'Total Operating Hours'}
            </h4>
            <div className="text-2xl font-bold text-purple-600">
              {report?.utilization?.total_operating_hours?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-gray-500">
              {t('viewReport.common.hours') || 'Hours'}
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
          <div className="text-3xl mb-3">📊</div>
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">
              {t('viewReport.utilization.averageHours') || 'Average Hours per Asset'}
            </h4>
            <div className="text-2xl font-bold text-orange-600">
              {report?.utilization?.average_operating_hours_per_asset?.toFixed(1) || 0}
            </div>
            <div className="text-sm text-gray-500">
              {t('viewReport.common.hours') || 'Hours'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const getTabTitle = () => {
    switch (activeTab) {
      case 'condition':
        return t('viewReport.tabs.condition.title') || 'Asset Condition Report';
      case 'maintenance':
        return t('viewReport.tabs.maintenance.title') || 'Maintenance Report';
      case 'utilization':
        return t('viewReport.tabs.utilization.title') || 'Utilization Report';
      default:
        return t('viewReport.title') || 'Asset Report';
    }
  };

  const tabs = [
    {
      id: 'condition',
      label: t('viewReport.tabLabels.condition') || 'Condition',
      icon: '🔍'
    },
    {
      id: 'utilization',
      label: t('viewReport.tabLabels.utilization') || 'Utilization',
      icon: '📊'
    }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false);
    }
  };

  if (!showModal) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-white">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {getTabTitle()}
          </h2>
          <button
            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
            onClick={() => setShowModal(false)}
          >
            <FaTimes className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-140px)] bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <div className="text-gray-600">{t('viewReport.loading') || 'Loading...'}</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                {error}
              </div>
            </div>
          ) : (
            <>
              {/* Tabs Container */}
              <div className="mb-6">
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <span className="mr-2 text-base">{tab.icon}</span>
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="bg-white">
                {activeTab === 'condition' && renderConditionReport()}
                {activeTab === 'utilization' && renderUtilizationReport()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewReport;